import registry from 'virtual:povli-content'
import type { ChatLanding, ChatNode, ChatStoryVersion, ContentRegistry, Landing, ReaderStoryVersion, ResolvedChatStory, ResolvedExperience, ResolvedReader } from '../types/content'

const content: ContentRegistry = registry

export function resolveLanding(landingId: string): ResolvedExperience | undefined {
  const landing = content.landings.find((candidate) => candidate.landing_id === landingId && candidate.status === 'active')
  if (!landing) return undefined

  const story = content.stories.find(
    (candidate) => candidate.story_id === landing.story && candidate.version === landing.version && candidate.status === 'active',
  )
  if (!story) return undefined

  if (story.mode === 'chat' && landing.start_node) return createChatStory(story, landing.start_node, landing.lock, landing.entry_mode, false, landing, landing.entry_context)
  if (story.mode === 'reader' && landing.start_block) {
    const lock = story.locks.find((candidate) => candidate.id === landing.lock)
    return lock ? createReader(story, lock, landing.start_block, landing.entry_mode, false, landing, landing.entry_context) : undefined
  }
  return undefined
}

export function resolvePreview(slug: string, version?: string, lockId?: string, startPoint?: string): ResolvedExperience | undefined {
  const versions = content.stories.filter((candidate) => candidate.slug === slug && candidate.status !== 'archived')
  const story = version ? versions.find((candidate) => candidate.version === version) : versions.at(0)
  if (!story) return undefined

  if (story.mode === 'chat') {
    const lockNode = lockId ? story.nodes.find((node) => node.id === lockId && node.type === 'lock') : story.nodes.find((node) => node.type === 'lock')
    const entryNode = startPoint ?? story.start_node
    return lockNode ? createChatStory(story, entryNode, lockNode.id, startPoint ? 'anchor' : 'start', true) : undefined
  }

  const lock = lockId ? story.locks.find((candidate) => candidate.id === lockId) : story.locks.at(0)
  const entryBlock = startPoint ?? story.blocks.at(0)?.id
  if (!lock || !entryBlock) return undefined
  return createReader(story, lock, entryBlock, startPoint ? 'anchor' : 'start', true)
}

function createReader(
  story: ReaderStoryVersion,
  lock: ReaderStoryVersion['locks'][number],
  entryBlock: string,
  entryMode: 'start' | 'anchor',
  preview: boolean,
  landing?: Landing,
  entryContext?: string,
): ResolvedReader | undefined {
  const startIndex = story.blocks.findIndex((block) => block.id === entryBlock)
  const lockIndex = story.blocks.findIndex((block) => block.id === lock.afterBlock)
  if (startIndex < 0 || lockIndex < startIndex) return undefined
  const visibleBlocks = story.blocks.slice(startIndex, lockIndex + 1)
  return {
    story,
    lock,
    html: visibleBlocks.map((block) => block.html).join(''),
    entryMode,
    entryPoint: entryBlock,
    entryBlock,
    entryContext,
    lockPoint: lock.afterBlock,
    lockBlock: lock.afterBlock,
    contentDistance: visibleBlocks.length,
    landing,
    preview,
  }
}

function nodeTargets(node: ChatNode): string[] {
  if (node.type === 'choice') return node.options.map((option) => option.next)
  if ('next' in node && node.next) return [node.next]
  return []
}

function shortestDistance(story: ChatStoryVersion, startNode: string, lockNode: string): number {
  const queue: Array<[string, number]> = [[startNode, 1]]
  const visited = new Set<string>()
  while (queue.length) {
    const [nodeId, distance] = queue.shift()!
    if (nodeId === lockNode) return distance
    if (visited.has(nodeId)) continue
    visited.add(nodeId)
    const node = story.nodes.find((candidate) => candidate.id === nodeId)
    if (node) queue.push(...nodeTargets(node).map((target) => [target, distance + 1] as [string, number]))
  }
  return 0
}

function createChatStory(
  story: ChatStoryVersion,
  startNode: string,
  lockNode: string,
  entryMode: 'start' | 'anchor',
  preview: boolean,
  landing?: Landing,
  entryContext?: string,
): ResolvedChatStory | undefined {
  if (!story.nodes.some((node) => node.id === startNode) || !story.nodes.some((node) => node.id === lockNode && node.type === 'lock')) return undefined
  const contentDistance = shortestDistance(story, startNode, lockNode)
  if (!contentDistance) return undefined
  const chatLanding = landing as ChatLanding | undefined
  const preloadedNodes = (chatLanding?.preload_nodes ?? [])
    .map((nodeId) => story.nodes.find((node) => node.id === nodeId))
    .filter((node): node is ChatNode => Boolean(node))
  return {
    story,
    startNode,
    lockNode,
    preloadedNodes,
    firstInteractiveNode: chatLanding?.first_interactive_node ?? startNode,
    entryMode,
    entryPoint: startNode,
    entryContext,
    lockPoint: lockNode,
    contentDistance,
    landing,
    preview,
  }
}

export function getRegistrySummary() {
  return {
    stories: content.stories.map((story) => story.mode === 'reader'
      ? { story_id: story.story_id, slug: story.slug, version: story.version, title: story.title, mode: story.mode, status: story.status, locks: story.locks }
      : { story_id: story.story_id, slug: story.slug, version: story.version, title: story.title, mode: story.mode, status: story.status, locks: story.nodes.filter((node) => node.type === 'lock').map((node) => ({ id: node.id })) }),
    landings: content.landings,
  }
}
